import { Box, Button, Dialog, IconButton, LucideIcon, Separator, Stack, Text } from "@sayaww/nomimono"
import { Squircle } from "corner-smoothing"
import { useFlags } from "flagsmith/react"
import { motion } from "framer-motion"
import { useAtom } from "jotai"
import React from "react"
import toast from "react-hot-toast"
import { useParams } from "react-router-dom"
import { useVaultDetail } from "src/pages/VaultDetail/useVaultDetail"
import { EventGroup, EventName, segmentService } from "src/service/segment/SegmentService"
import { openQueueStatus } from "src/util/uiAtom"
import { QueueDetails } from "./QueueDetails"
import { Stat } from "./Stat"

const MotionBox = motion(Box)
const MotionButton = motion(Button)
export function QueueStatus() {
    // Fetch all kinds of data
    const { vaultSlug } = useParams()
    const vaultData = useVaultDetail(vaultSlug)
    const { withdrawFromShowerRoom } = useVaultDetail(vaultSlug)
    const vaultId = vaultData.vault?.id

    const depositAssetSymbol = vaultData.vault?.depositAsset.symbol
    const maxDepositPerUser = vaultData.vault?.srMaxDepositPerUser
    const waitingListLength = vaultData.vault?.srWaitingListLength
    const myWaitingIndex = vaultData.vault?.srMyWaitingIndex
    // -1 -> not in queue
    // 0 -> first in queue
    const myWaitingAmount = vaultData.vault?.srMyWaitingAmount

    // TODO: [ShowerRoom] update isShowerRoomEnabled when flagsmith service is back
    const flags = useFlags(["shower_room"])
    const isShowerRoomEnabled = true || flags.shower_room.enabled

    const isUserInQueue = myWaitingIndex !== undefined && myWaitingIndex >= 0 ? true : false

    // Handle dialog open/close
    const [open, setOpen] = useAtom(openQueueStatus)

    // Form data
    const withdrawFromQueue = async () => {
        if (!vaultId) {
            toast.error(`Vault is not available`)
            return
        }

        toast.loading(`Withdrawing from queue...`, {
            id: "withdraw-from-queue",
        })

        const txResult = await withdrawFromShowerRoom(vaultId)

        if (txResult.data !== null) {
            toast.dismiss("withdraw-from-queue")
            toast.success(`Successfully withdrew from queue!`)
            setOpen(false)
        }

        segmentService.track({
            eventGroup: EventGroup.INTERACTION,
            eventName: EventName.WITHDRAW_SHOWER_ROOM,
            payload: { vaultId: vaultId },
            description: "User withdraws from shower room (always 100%)",
        })

        toast(`Withdrawing from queue...`)
    }

    if (!isShowerRoomEnabled) return null
    return (
        <>
            <Dialog.Root open={open}>
                <Dialog.Trigger asChild>
                    <MotionBox
                        onClick={() => setOpen(true)}
                        css={{
                            cursor: "pointer",
                            background: isUserInQueue ? "#3CEAAA" : "#e4e2e4",
                            padding: "8px 24px 8px 24px",
                            display: "flex",
                            "flex-direction": "row",
                            gap: "16px",
                            alignItems: "center",
                            justifyContent: "center",
                            alignSelf: "stretch",
                            flexShrink: 0,
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        <PeopleIcon />
                        {isUserInQueue && myWaitingIndex !== undefined ? (
                            <Text size={"label"}>
                                You are{" "}
                                <span style={{ fontWeight: "bold" }}>
                                    {myWaitingIndex + 1} of {waitingListLength}
                                </span>{" "}
                                in the queue.
                            </Text>
                        ) : (
                            <Text size={"label"}>{waitingListLength} people in the queue</Text>
                        )}
                        <div style={{ flex: 1 }} />
                        <Text size={"caption"}>View Details</Text>
                    </MotionBox>
                </Dialog.Trigger>
                <Dialog.Portal>
                    <Dialog.Overlay>
                        <Dialog.Content asChild>
                            <Squircle
                                cornerRadius={30}
                                cornerSmoothing={0.6}
                                style={{
                                    padding: "24px",
                                    width: "100%",
                                    maxWidth: "540px",
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                    backgroundColor: "white",
                                }}
                            >
                                <Dialog.Close asChild>
                                    <Box
                                        onClick={() => setOpen(false)}
                                        css={{
                                            position: "absolute",
                                            top: "$4",
                                            right: "$4",
                                            zIndex: "32",
                                        }}
                                    >
                                        <IconButton style={{ borderRadius: "9999px" }} name="x" intent="ghost" />
                                    </Box>
                                </Dialog.Close>
                                <Box
                                    css={{
                                        flex: 1,
                                        flexDirection: "column",
                                    }}
                                >
                                    <Text size="title3">Queue Status</Text>
                                    {isUserInQueue && (
                                        <Box
                                            css={{
                                                display: "flex",
                                                flexDirection: "row",
                                                gap: 20,
                                                alignItems: "center",
                                                justifyContent: "center",
                                                marginTop: 24,
                                                px: 24,
                                                py: 12,
                                                borderRadius: "$2xl",
                                                bgTone3: "green",
                                            }}
                                        >
                                            <LucideIcon name="user-check" size={24} />
                                            <Text size={"label"}>
                                                Your funds have been successfully added to the queue. No further actions
                                                required.
                                            </Text>
                                        </Box>
                                    )}
                                    <Stack gap={0} mt={"8"}>
                                        <Box
                                            css={{
                                                flex: 1,
                                                flexWrap: "wrap",
                                                rowGap: "$3",
                                                columnGap: "$12",
                                            }}
                                        >
                                            {isUserInQueue && (
                                                <>
                                                    <Stat
                                                        label={"Position"}
                                                        value={`${(myWaitingIndex || 0) + 1}`}
                                                        subValue={`of ${waitingListLength}`}
                                                    />
                                                    <Stat
                                                        label={"Amount in Queue"}
                                                        value={myWaitingAmount?.toFixed(4)}
                                                        subValue={depositAssetSymbol}
                                                    />
                                                </>
                                            )}
                                            {!isUserInQueue && (
                                                <>
                                                    <Stat label={"In Queue"} value={waitingListLength} />
                                                </>
                                            )}
                                            <Stat
                                                label={"Est. APR in Queue"}
                                                value={"1.5~5%"}
                                                bottomValue={<Text size="caption">Powered by Yearn</Text>}
                                            />
                                        </Box>
                                        <Separator />

                                        <Text size="label" css={{ pt: 16, ml: "$4", color: "$gray9" }}>
                                            <QueueDetails
                                                maxDepositPerUser={maxDepositPerUser?.toFixed() ?? ""}
                                                depositAssetSymbol={depositAssetSymbol ?? ""}
                                            />
                                        </Text>
                                    </Stack>
                                </Box>

                                {isUserInQueue && (
                                    <MotionBox
                                        layout
                                        css={{
                                            flex: 1,
                                            gap: 8,
                                            pt: "$4",
                                        }}
                                    >
                                        <MotionButton
                                            onClick={() => {
                                                withdrawFromQueue()
                                            }}
                                            css={{
                                                cursor: "pointer",
                                                alignItems: "center",
                                                justifyContent: "center",
                                            }}
                                            leftIcon={
                                                <Box css={{ top: 2, position: "relative" }}>
                                                    <LucideIcon size={16} name="trash-2" />
                                                </Box>
                                            }
                                            tone="red"
                                            intent="secondary"
                                            size="lg"
                                        >
                                            <div>Withdraw from queue</div>
                                        </MotionButton>
                                    </MotionBox>
                                )}
                            </Squircle>
                        </Dialog.Content>{" "}
                    </Dialog.Overlay>
                </Dialog.Portal>
            </Dialog.Root>
        </>
    )
}
export const PeopleIcon = (props: React.JSX.IntrinsicAttributes & React.SVGProps<SVGSVGElement>) => {
    return (
        <svg
            style={{ flexShrink: 0 }}
            width="25"
            height="24"
            viewBox="0 0 25 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            {...props}
        >
            <path
                d="M23.45 14.1002C23.3712 14.1593 23.2816 14.2023 23.1862 14.2267C23.0908 14.2511 22.9915 14.2566 22.894 14.2426C22.7965 14.2287 22.7027 14.1957 22.6179 14.1455C22.5332 14.0953 22.4591 14.029 22.4 13.9502C21.948 13.3425 21.3596 12.8495 20.6822 12.5108C20.0048 12.1721 19.2574 11.9972 18.5 12.0002C18.3526 12.0002 18.2083 11.9567 18.0855 11.8751C17.9626 11.7936 17.8664 11.6776 17.8091 11.5417C17.7702 11.4495 17.7501 11.3503 17.7501 11.2502C17.7501 11.15 17.7702 11.0509 17.8091 10.9586C17.8664 10.8227 17.9626 10.7068 18.0855 10.6252C18.2083 10.5437 18.3526 10.5002 18.5 10.5002C18.9209 10.5001 19.3332 10.3821 19.6903 10.1594C20.0474 9.93677 20.3349 9.61844 20.5201 9.24058C20.7054 8.86273 20.7809 8.4405 20.7382 8.02185C20.6955 7.60321 20.5363 7.20493 20.2786 6.87225C20.0209 6.53958 19.675 6.28584 19.2803 6.13987C18.8856 5.9939 18.4579 5.96154 18.0458 6.04646C17.6336 6.13139 17.2535 6.3302 16.9487 6.62031C16.6439 6.91043 16.4265 7.28021 16.3213 7.68767C16.2967 7.78308 16.2535 7.87271 16.1942 7.95144C16.135 8.03017 16.0608 8.09645 15.9759 8.14651C15.891 8.19656 15.7971 8.22941 15.6995 8.24318C15.602 8.25694 15.5026 8.25135 15.4072 8.22673C15.3118 8.20211 15.2222 8.15893 15.1435 8.09967C15.0647 8.04041 14.9984 7.96622 14.9484 7.88134C14.8983 7.79646 14.8655 7.70256 14.8517 7.60498C14.838 7.50741 14.8435 7.40808 14.8682 7.31267C15.0142 6.74765 15.2901 6.22451 15.6738 5.78487C16.0576 5.34523 16.5387 5.00124 17.0788 4.78025C17.619 4.55926 18.2032 4.46738 18.7851 4.51191C19.367 4.55645 19.9304 4.73618 20.4306 5.0368C20.9308 5.33742 21.3539 5.75063 21.6663 6.24356C21.9787 6.73648 22.1718 7.29552 22.2301 7.87618C22.2884 8.45684 22.2104 9.0431 22.0023 9.58831C21.7942 10.1335 21.4617 10.6226 21.0313 11.0167C22.0511 11.4583 22.9377 12.159 23.6029 13.0492C23.6619 13.1282 23.7049 13.2181 23.7292 13.3137C23.7535 13.4093 23.7587 13.5088 23.7445 13.6064C23.7303 13.704 23.697 13.7979 23.6465 13.8826C23.5959 13.9673 23.5292 14.0413 23.45 14.1002ZM18.3988 19.8752C18.4531 19.9605 18.4895 20.056 18.5059 20.1558C18.5223 20.2556 18.5184 20.3577 18.4943 20.456C18.4703 20.5542 18.4266 20.6466 18.3659 20.7275C18.3052 20.8084 18.2288 20.8762 18.1412 20.9268C18.0536 20.9775 17.9567 21.0099 17.8563 21.022C17.7559 21.0342 17.654 21.026 17.5569 20.9978C17.4597 20.9696 17.3693 20.9221 17.291 20.8581C17.2127 20.794 17.1482 20.7148 17.1013 20.6252C16.6288 19.8252 15.956 19.1622 15.1491 18.7016C14.3422 18.241 13.4291 17.9988 12.5 17.9988C11.5709 17.9988 10.6579 18.241 9.85102 18.7016C9.04412 19.1622 8.37125 19.8252 7.89879 20.6252C7.8519 20.7148 7.78737 20.794 7.70908 20.8581C7.63078 20.9221 7.54034 20.9696 7.44319 20.9978C7.34605 21.026 7.2442 21.0342 7.14379 21.022C7.04338 21.0099 6.94647 20.9775 6.85889 20.9268C6.77132 20.8762 6.69489 20.8084 6.6342 20.7275C6.57352 20.6466 6.52983 20.5542 6.50576 20.456C6.4817 20.3577 6.47775 20.2556 6.49417 20.1558C6.51059 20.056 6.54702 19.9605 6.60129 19.8752C7.32841 18.6258 8.4371 17.6427 9.76441 17.0702C9.01753 16.4983 8.46863 15.7068 8.19485 14.8069C7.92108 13.9069 7.9362 12.9438 8.23808 12.053C8.53997 11.1621 9.11346 10.3882 9.87792 9.84005C10.6424 9.29194 11.5594 8.99716 12.5 8.99716C13.4407 8.99716 14.3577 9.29194 15.1222 9.84005C15.8866 10.3882 16.4601 11.1621 16.762 12.053C17.0639 12.9438 17.079 13.9069 16.8052 14.8069C16.5315 15.7068 15.9825 16.4983 15.2357 17.0702C16.563 17.6427 17.6717 18.6258 18.3988 19.8752ZM12.5 16.5002C13.0934 16.5002 13.6734 16.3242 14.1667 15.9946C14.6601 15.6649 15.0446 15.1964 15.2717 14.6482C15.4987 14.1 15.5582 13.4968 15.4424 12.9149C15.3266 12.333 15.0409 11.7984 14.6214 11.3788C14.2018 10.9593 13.6673 10.6736 13.0853 10.5578C12.5034 10.4421 11.9002 10.5015 11.352 10.7285C10.8038 10.9556 10.3353 11.3401 10.0056 11.8335C9.67599 12.3268 9.50004 12.9068 9.50004 13.5002C9.50004 14.2958 9.81611 15.0589 10.3787 15.6215C10.9413 16.1841 11.7044 16.5002 12.5 16.5002ZM7.25004 11.2502C7.25004 11.0513 7.17102 10.8605 7.03037 10.7198C6.88972 10.5792 6.69895 10.5002 6.50004 10.5002C6.07922 10.5001 5.66685 10.3821 5.30976 10.1594C4.95267 9.93677 4.66518 9.61844 4.47994 9.24058C4.29471 8.86273 4.21915 8.4405 4.26185 8.02185C4.30455 7.60321 4.4638 7.20493 4.72152 6.87225C4.97923 6.53958 5.32507 6.28584 5.71976 6.13987C6.11445 5.9939 6.54216 5.96154 6.95432 6.04646C7.36648 6.13139 7.74656 6.3302 8.05139 6.62031C8.35622 6.91043 8.57359 7.28021 8.67879 7.68767C8.72852 7.88036 8.85276 8.04541 9.02418 8.14651C9.1956 8.2476 9.40016 8.27646 9.59285 8.22673C9.78555 8.177 9.9506 8.05276 10.0517 7.88134C10.1528 7.70992 10.1816 7.50536 10.1319 7.31267C9.98589 6.74765 9.71002 6.22451 9.32624 5.78487C8.94246 5.34523 8.46137 5.00124 7.92125 4.78025C7.38112 4.55926 6.79688 4.46738 6.215 4.51191C5.63312 4.55645 5.06967 4.73618 4.56947 5.0368C4.06928 5.33742 3.64614 5.75063 3.33375 6.24356C3.02135 6.73648 2.82831 7.29552 2.76998 7.87618C2.71165 8.45684 2.78965 9.0431 2.99777 9.58831C3.20589 10.1335 3.53838 10.6226 3.96879 11.0167C2.94999 11.4587 2.06446 12.1594 1.40004 13.0492C1.28057 13.2084 1.2292 13.4084 1.25725 13.6054C1.28529 13.8024 1.39044 13.9802 1.54957 14.0997C1.7087 14.2192 1.90877 14.2705 2.10577 14.2425C2.30278 14.2144 2.48057 14.1093 2.60004 13.9502C3.05209 13.3425 3.64049 12.8495 4.31788 12.5108C4.99526 12.1721 5.74271 11.9972 6.50004 12.0002C6.69895 12.0002 6.88972 11.9211 7.03037 11.7805C7.17102 11.6398 7.25004 11.4491 7.25004 11.2502Z"
                fill="currentColor"
            />
        </svg>
    )
}
